import { applyPrimitives, expandAction, type Action, type Contact, type Replica, type Synced, type SyncResponse } from '@crmsy/shared';
import { todayIso } from './today.js';
import { ApiError, fetchSync } from './api.js';
import { clearReplica, db, getCachedUser, getLastRev, provisionalLogId, setMeta } from './db.js';

export type ConnectionState = 'offline' | 'connecting' | 'live';

type StateListener = (s: ConnectionState) => void;

/**
 * Pull on demand and on every SSE `rev` heartbeat (§5.3, §5.4). One instance per tab.
 * Reads never wait on this: components render from Dexie and re-render via live queries.
 */
export class SyncClient {
  private es: EventSource | null = null;
  private pulling: Promise<void> | null = null;
  private pullAgain = false;
  private backoff = 1000;
  private state: ConnectionState = 'connecting';
  private readonly listeners = new Set<StateListener>();
  private stopped = false;
  onUnauthenticated: () => void = () => {};

  getState(): ConnectionState {
    return this.state;
  }

  subscribe(fn: StateListener): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => this.listeners.delete(fn);
  }

  private setState(s: ConnectionState) {
    if (this.state === s) return;
    this.state = s;
    for (const fn of this.listeners) fn(s);
  }

  start(): void {
    this.stopped = false;
    void this.pull();
    this.connect();
    window.addEventListener('online', this.onOnline);
    document.addEventListener('visibilitychange', this.onVisible);
  }

  stop(): void {
    this.stopped = true;
    this.es?.close();
    this.es = null;
    window.removeEventListener('online', this.onOnline);
    document.removeEventListener('visibilitychange', this.onVisible);
  }

  private onOnline = () => {
    void this.pull();
    if (!this.es) this.connect();
  };

  private onVisible = () => {
    if (document.visibilityState === 'visible') void this.pull();
  };

  private connect(): void {
    if (this.stopped) return;
    this.setState('connecting');
    const es = new EventSource('/api/events');
    this.es = es;
    es.addEventListener('rev', (ev) => {
      this.backoff = 1000;
      this.setState('live');
      const rev = Number((ev as MessageEvent).data);
      void this.pullIfBehind(rev);
    });
    es.addEventListener('ping', () => this.setState('live'));
    es.onerror = () => {
      es.close();
      this.es = null;
      this.setState('offline');
      if (this.stopped) return;
      // EventSource cannot see status codes; check the session with a cheap pull.
      setTimeout(() => {
        void this.pull().finally(() => this.connect());
      }, this.backoff);
      this.backoff = Math.min(this.backoff * 2, 30_000);
    };
  }

  private async pullIfBehind(rev: number): Promise<void> {
    if ((await getLastRev()) < rev) await this.pull();
  }

  /** Fetch rows with `rev > lastRev` and apply them atomically. Coalesces concurrent calls. */
  pull(): Promise<void> {
    if (this.pulling) {
      this.pullAgain = true;
      return this.pulling;
    }
    this.pulling = this.doPull().finally(() => {
      this.pulling = null;
      if (this.pullAgain) {
        this.pullAgain = false;
        void this.pull();
      }
    });
    return this.pulling;
  }

  private async doPull(): Promise<void> {
    if (this.stopped) return;
    let res: SyncResponse;
    try {
      res = await fetchSync(await getLastRev());
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        this.onUnauthenticated();
        return;
      }
      this.setState('offline');
      return;
    }
    await applySync(res);
    this.setState(this.es ? 'live' : 'connecting');
  }
}

export async function applySync(res: SyncResponse): Promise<void> {
  await db.transaction('rw', [db.contacts, db.log, db.schema, db.people, db.drafts, db.meta, db.outbox], async () => {
    if (res.full) {
      await clearReplica();
    }
    if (res.contacts.length) {
      // A contact with queued actions shows the server's row plus my pending changes on top (§5.4, §5.7).
      const queued = (await db.outbox.where('status').anyOf('queued', 'sending').toArray()).sort((a, b) => a.actionId.localeCompare(b.actionId));
      const busy = new Set(queued.map((o) => o.contactId));
      const rows: Synced<Contact>[] = [];
      for (const c of res.contacts) rows.push(busy.has(c.id) ? await withPending(c, queued.filter((o) => o.contactId === c.id)) : c);
      await db.contacts.bulkPut(rows);
    }
    if (res.log.length) {
      // The server's row for an action replaces the provisional one the outbox wrote (§5.4 step 2).
      const provisional = res.log.filter((e) => e.actionId).map((e) => provisionalLogId(e.actionId!));
      if (provisional.length) await db.log.bulkDelete(provisional);
      await db.log.bulkPut(res.log);
    }
    if (res.drafts?.length) await db.drafts.bulkPut(res.drafts);
    if (res.schema) {
      await db.schema.clear();
      await db.schema.bulkPut(Object.entries(res.schema).map(([property, options]) => ({ property, options })));
    }
    if (res.people) {
      await db.people.clear();
      await db.people.bulkPut(res.people);
    }
    await setMeta('lastRev', res.rev);
  });
}

/** Re-apply queued actions to an authoritative row so optimistic edits survive a pull. */
async function withPending(row: Synced<Contact>, items: { actionId: string; type: string; payload: unknown; at: string }[]): Promise<Synced<Contact>> {
  const me = await getCachedUser();
  if (!me) return row;
  const replica: Replica = { contacts: new Map([[row.id, row]]), log: new Map() };
  for (const item of items) {
    const action = { actionId: item.actionId, at: item.at, type: item.type, payload: item.payload, expected: {} } as Action;
    try {
      const entry = action.type === 'revert' ? ((await db.log.get(action.payload.entryId)) ?? null) : null;
      const contact = replica.contacts.get(row.id) ?? null;
      const x = expandAction(action, { contact, entry, today: todayIso(), me: { notionUserId: me.notionUserId } });
      applyPrimitives(replica, x.primitives.filter((p) => p.kind !== 'archiveLog' && p.kind !== 'restoreLog'), item.at);
    } catch {
      /* the server will answer this action with 4xx; the row stays authoritative */
    }
  }
  return { ...(replica.contacts.get(row.id) ?? row), rev: row.rev };
}

export const syncClient = new SyncClient();
